import { Button, Heading, LuIcon, radiusCva, useLayoutContext, useProvider } from "@overmap-ai/blocks"
import { selectCurrentUser } from "@overmap-ai/core"
import { Link, useNavigate, type ValidateLinkOptions } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { UserSettingsDropdownMenu } from "../../components/DropdownMenus/UserSettingsDropdownMenu"
import { UserIcon } from "../../components/Icons/UserIcon"
import { navigationLink, navigationLinkIcon } from "../../cva"
import { SLIDE_OUT_IDS } from "../../layouts"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { selectActiveOrganizationId } from "../../store/selectors"

type RouteInfo = {
	label: string
	to: ValidateLinkOptions["to"]
	icon: (typeof modelIconMapping)[keyof typeof modelIconMapping]
	activeOptions?: ValidateLinkOptions["activeOptions"]
}

const accountRoutes = [
	{
		label: "Profile",
		to: "/$organizationId/settings/accounts/profile",
		icon: modelIconMapping.user,
		activeOptions: { exact: true },
	},
	{
		label: "Preferences",
		to: "/$organizationId/settings/accounts/preferences",
		icon: modelIconMapping.preferences,
		activeOptions: { exact: true },
	},
] satisfies RouteInfo[]

const organizationRoutes = [
	{
		label: "General",
		to: "/$organizationId/settings/organization",
		icon: modelIconMapping.settings,
		activeOptions: { exact: true },
	},
	{
		label: "Members",
		to: "/$organizationId/settings/organization/members",
		icon: modelIconMapping.group,
		activeOptions: { exact: true },
	},
	{
		label: "Teams",
		to: "/$organizationId/settings/teams",
		icon: modelIconMapping.team,
		activeOptions: { exact: false },
	},
	{
		label: "Projects",
		to: "/$organizationId/settings/projects",
		icon: modelIconMapping.project,
		activeOptions: { exact: false },
	},
	{
		label: "Email domains",
		to: "/$organizationId/settings/organization/email-domains",
		icon: modelIconMapping.globe,
		activeOptions: { exact: true },
	},
] satisfies RouteInfo[]

const modelsRoutes = [
	{
		label: "Asset types",
		to: "/$organizationId/settings/asset-types",
		icon: modelIconMapping.assetType,
		activeOptions: { exact: false },
	},
	{
		label: "Procedure types",
		to: "/$organizationId/settings/procedure-types",
		icon: modelIconMapping.procedureType,
		activeOptions: { exact: false },
	},
	{
		label: "Issue types",
		to: "/$organizationId/settings/issue-types",
		icon: modelIconMapping.issue,
		activeOptions: { exact: false },
	},
	{
		label: "Forms",
		to: "/$organizationId/settings/forms",
		icon: modelIconMapping.form,
		activeOptions: { exact: false },
	},
] satisfies RouteInfo[]

export const SettingsNavigation = memo(() => {
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)

	if (!activeOrganizationId) {
		throw new Error("No active organization found")
	}

	const user = useAppSelector(selectCurrentUser)
	const { radius } = useProvider()
	const { setOpen, small } = useLayoutContext()
	const navigate = useNavigate()

	const handleSettingsBack = useCallback(() => {
		void navigate({
			from: "/$organizationId",
			to: "/$organizationId",
		})
	}, [navigate])

	const handleLinkClick = useCallback(() => {
		if (small) setOpen(SLIDE_OUT_IDS.LEFT, false)
	}, [setOpen, small])

	return (
		<nav className="flex w-full flex-col gap-4 p-4">
			<div className="flex w-full gap-2">
				<Button
					className="grow justify-start"
					variant="fill"
					accentColor="base"
					onClick={handleSettingsBack}
					size="md"
				>
					<LuIcon icon={modelIconMapping.arrowLeft} />
					Back to app
				</Button>

				<UserSettingsDropdownMenu size="sm">
					<UserIcon userId={user?.id} size="md" />
				</UserSettingsDropdownMenu>
			</div>

			<div className="flex h-max w-full flex-col gap-4">
				<section className="flex h-max w-full flex-col gap-2">
					<Heading accentColor="base" className="px-2">
						Account
					</Heading>
					<div className="flex h-max w-full flex-col gap-0.5">
						{accountRoutes.map((route) => (
							<Link
								key={route.label}
								className={cx(navigationLink(), radiusCva({ radius, maxLarge: false }))}
								activeOptions={route.activeOptions}
								from="/$organizationId"
								to={route.to}
								onClick={handleLinkClick}
								draggable={false}
							>
								<LuIcon icon={route.icon} className={navigationLinkIcon()} />
								<span className="truncate">{route.label}</span>
							</Link>
						))}
					</div>
				</section>

				<section className="flex h-max w-full flex-col gap-2">
					<Heading accentColor="base" className="px-2">
						Organization
					</Heading>
					<div className="flex h-max w-full flex-col gap-0.5">
						{organizationRoutes.map((route) => (
							<Link
								key={route.label}
								className={cx(navigationLink(), radiusCva({ radius, maxLarge: false }))}
								activeOptions={route.activeOptions}
								from="/$organizationId"
								to={route.to}
								onClick={handleLinkClick}
								draggable={false}
							>
								<LuIcon icon={route.icon} className={navigationLinkIcon()} />
								<span className="truncate">{route.label}</span>
							</Link>
						))}
					</div>
				</section>

				<section className="flex h-max w-full flex-col gap-2">
					<Heading accentColor="base" className="px-2">
						Models
					</Heading>
					<div className="flex h-max w-full flex-col gap-0.5">
						{modelsRoutes.map((route) => (
							<Link
								key={route.label}
								className={cx(navigationLink(), radiusCva({ radius, maxLarge: false }))}
								activeOptions={route.activeOptions}
								from="/$organizationId"
								to={route.to}
								onClick={handleLinkClick}
								draggable={false}
							>
								<LuIcon icon={route.icon} className={navigationLinkIcon()} />
								<span className="truncate">{route.label}</span>
							</Link>
						))}
					</div>
				</section>
			</div>
		</nav>
	)
})
SettingsNavigation.displayName = "SettingsNavigation"
